import { ReportingType } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import Input from '@/components/basicComponents/input';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Select from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { useErrorMessages } from '@/hooks/i18n';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { themeVar } from '@/styles/theme';
import { ApolloQueryResult, useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { ReportingType as ReportingTypeEnum } from '@trace-8/trace8-schema';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { z } from 'zod/v4';
import { reportingTypesEnumMap } from '../constants';
import { CREATE_TENANT_MUTATION } from '../queries';
import { IAdvisoryFirms } from '../types';

const reportingTemplateOptions = Object.entries(reportingTypesEnumMap).map(([key, value]) => createSimpleSelectOption(key, value));

const defaultCreateTenantFormValues: CreateTenantType = {
  name: '',
  adminEmail: '',
  adminFirstName: '',
  adminLastName: '',
  reportingType: null
};

const createTenantSchema = z.object({
  name: z.string().min(1, { message: 'Required' }),
  adminEmail: z.string().min(1, { message: 'Required' }).email(),
  adminFirstName: z.string().min(1, { message: 'Required' }),
  adminLastName: z.string().min(1, { message: 'Required' }),
  reportingType: z.union([z.nativeEnum(ReportingTypeEnum), z.literal('')])
});
type CreateTenantType = z.infer<typeof createTenantSchema>;

interface AddTenantModalProps {
  isOpen: boolean;
  refetchTenants: () => Promise<ApolloQueryResult<{ advisoryFirms: IAdvisoryFirms }>>;
  onClose: () => void;
}

const AddTenantModal = ({ isOpen, refetchTenants, onClose }: AddTenantModalProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();
  const { control, handleSubmit } = useForm<CreateTenantType>({
    defaultValues: defaultCreateTenantFormValues,
    resolver: zodResolver(createTenantSchema)
  });

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [createTenant] = useMutation(CREATE_TENANT_MUTATION);

  const onSave = async (data: CreateTenantType) => {
    try {
      setErrorMessage(null);
      setLoading(true);

      const { name, adminEmail, adminFirstName, adminLastName, reportingType } = data;

      await createTenant({
        variables: {
          data: {
            name,
            adminEmail,
            adminFirstName,
            adminLastName,
            ...(!!reportingType && { reportingType: getEnumKeyByValue(ReportingTypeEnum, reportingType) as ReportingType })
          }
        }
      });

      await refetchTenants();
      onClose();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title="Add New Advisory Firm"
      confirmButton={{
        name: 'Save',
        disabled: loading,
        onClick: handleSubmit(onSave)
      }}
      showCancelButton={!loading}
      isUseOutsideClick={!loading}
      showCloseIcon={!loading}
      showRequiredFields={!loading}
    >
      {loading ? (
        <ModalLoader customText="Creating new tenant..." />
      ) : (
        <>
          {errorMessage && (
            <Banner
              icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
              title="Error"
              description={errorMessage}
              bgColor={themeVar('context.error')}
            />
          )}
          <ModalContainer>
            <Controller
              name="name"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Input
                  label="Name"
                  value={value || ''}
                  onChange={(e) => {
                    onChange(e.target.value);
                  }}
                  errorText={error?.message}
                  placeholder="Name of the firm"
                  isRequiredField
                />
              )}
            />
            <Controller
              name="adminEmail"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Input
                  label="Primary Administrator Email"
                  value={value || ''}
                  onChange={(e) => {
                    onChange(e.target.value);
                  }}
                  errorText={error?.message}
                  placeholder="Valid email address"
                  isRequiredField
                />
              )}
            />
            <Controller
              name="adminFirstName"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Input
                  label="Primary Administrator First Name"
                  value={value || ''}
                  onChange={(e) => {
                    onChange(e.target.value);
                  }}
                  errorText={error?.message}
                  placeholder="First name of the user"
                  isRequiredField
                />
              )}
            />
            <Controller
              name="adminLastName"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Input
                  label="Primary Administrator Last Name"
                  value={value || ''}
                  onChange={(e) => {
                    onChange(e.target.value);
                  }}
                  errorText={error?.message}
                  placeholder="Last name of the user"
                  isRequiredField
                />
              )}
            />
            <Controller
              name="reportingType"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Select
                  label="Reporting Template"
                  value={value}
                  options={[{ label: 'None', value: '' }, ...reportingTemplateOptions]}
                  setValue={onChange}
                  errorText={error?.message}
                  isRequiredField
                />
              )}
            />
          </ModalContainer>
        </>
      )}
    </ModalWrappedContent>
  );
};

export default AddTenantModal;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 15px;
`;
